import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { IMAGES } from "@/lib/constants";

export default function About() {
  return (
    <div data-testid="page-about" className="bg-cream-50">
      {/* Intro */}
      <section className="relative overflow-hidden pt-8 md:pt-12 pb-16 md:pb-24">
        <div className="aura-bg opacity-60" />
        <div className="relative page-shell grid grid-cols-12 gap-10">
          <div className="hidden md:flex col-span-1 items-end pb-6">
            <div className="vertical-text text-lavender-700">Hakkında — Kim</div>
          </div>
          <div className="col-span-12 md:col-span-8 min-w-0">
            <div className="eyebrow mb-6">Tanışalım</div>
            <h1 className="max-w-full md:max-w-none font-display text-[15vw] sm:text-[12vw] md:text-[7vw] lg:text-[6.5rem] font-light text-midnight leading-[0.98] tracking-normal">
              Adım{" "}
              <em className="text-lavender-700 font-light">Yeliz</em>.
              <br />
              <span className="text-gold-soft italic font-light">Sezgisel</span>{" "}
              bir rehberim.
            </h1>
            <div className="h-px bg-gold w-32 mt-10 draw-line" />
          </div>
        </div>
      </section>

      {/* Biography */}
      <section className="py-14 md:py-20" data-testid="about-bio">
        <div className="page-shell grid grid-cols-12 gap-8 md:gap-10">
          <div className="col-span-12 md:col-span-5 md:col-start-1 order-2 md:order-1">
            <div className="img-zoom rounded-sm aspect-[3/4]">
              <img src={IMAGES.about} alt="Yeliz Kıran Pastırmacıoğlu" />
            </div>
            <div className="mt-4 text-[10px] tracking-[0.3em] uppercase text-lavender-700 italic">
              "Sessizlik en güçlü dildir." — Rumi
            </div>
          </div>
          <div className="col-span-12 md:col-span-6 md:col-start-7 order-1 md:order-2 space-y-8">
            <p className="font-display text-2xl sm:text-3xl md:text-4xl text-midnight font-light leading-[1.25]">
              Spiritüel yolculuğum, çocukluğumdan beri taşıdığım sezgisel
              farkındalıkla başladı.
            </p>
            <p className="text-lavender-700 leading-relaxed text-[15px]">
              Üniversite eğitiminde psikoloji okurken, modern bilim ile sezgisel
              dünya arasındaki incecik köprünün tam ortasında durduğumu fark
              ettim. Yıllar içinde Türkiye, Hollanda ve Hindistan'da eğitimler
              alarak; numeroloji, soy karması, frekanslarla şifa ve enerji
              çalışmaları alanlarında uzmanlaştım.
            </p>
            <p className="text-lavender-700 leading-relaxed text-[15px]">
              Bugün İstanbul merkezli kendi atölyemde bireysel seanslar verir,
              online danışmanlıklar yapar, küçük grup çalışmaları ve eğitim
              programları yürütürüm. Yaklaşımım her zaman aynıdır: yargısız bir
              alanda, sezgisel okumayı bilimsel bir çerçeveyle buluşturmak.
            </p>

            <div className="grid grid-cols-2 gap-6 pt-6 border-t border-cream-300">
              {[
                ["12+", "Yıl deneyim"],
                ["2000+", "Birebir seans"],
                ["6", "Uzmanlık alanı"],
                ["3", "Ülkede eğitim"],
              ].map(([n, l]) => (
                <div key={l}>
                  <div className="font-display text-4xl text-midnight font-light">
                    {n}
                  </div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-lavender-700 mt-1">
                    {l}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-20 md:py-32 bg-lavender-50/50 relative" data-testid="about-philosophy">
        <div className="page-shell">
          <div className="eyebrow mb-6">Felsefem</div>
          <h2 className="font-display text-4xl sm:text-5xl md:text-7xl font-light text-midnight leading-[0.98] max-w-4xl tracking-normal">
            Şifa, <em className="text-lavender-700">hatırlamaktır</em>.
            <br />
            Aydınlanma, <em className="text-gold-soft">dönüşmektir</em>.
          </h2>

          <div className="grid grid-cols-12 gap-6 md:gap-10 mt-12 md:mt-20">
            {[
              {
                n: "01",
                t: "Bütüncül Bakış",
                d: "Beden, zihin ve ruh ayrı değil. Çalışmalarımız her üç katmanı birden onurlandırır.",
              },
              {
                n: "02",
                t: "Bilim & Sezgi",
                d: "Modern psikoloji ve mindfulness, sezgisel okumayı destekleyen bir çerçeve sunar.",
              },
              {
                n: "03",
                t: "Kişiye Özel Yol",
                d: "Tek tip şablon yok. Her ruhun zamanı, ritmi ve haritası farklıdır.",
              },
            ].map((b) => (
              <div
                key={b.n}
                className="col-span-12 md:col-span-4 p-6 sm:p-8 md:p-10 bg-cream-50 border border-cream-300"
              >
                <div className="number-marker text-3xl">{b.n}</div>
                <h3 className="font-display text-3xl text-midnight mt-6 mb-4">
                  {b.t}
                </h3>
                <p className="text-lavender-700 leading-relaxed text-[15px]">
                  {b.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Education timeline */}
      <section className="py-20 md:py-32" data-testid="about-education">
        <div className="page-shell grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-4">
            <div className="eyebrow mb-6">Eğitim & Yetkinlik</div>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-light text-midnight leading-[1.05]">
              Bilgi · <em className="text-lavender-700">yolculukla</em>{" "}
              olgunlaşır.
            </h2>
          </div>
          <ol className="col-span-12 md:col-span-7 md:col-start-6 space-y-8 md:space-y-10 border-l border-cream-300 pl-7 sm:pl-10">
            {[
              ["2012", "İstanbul Üniversitesi · Psikoloji Lisans"],
              ["2014", "Hollanda · Sistemik Aile Dizimi Eğitmenliği"],
              ["2016", "Hindistan · Vedik Numeroloji & Çakra Çalışmaları"],
              ["2018", "Bireysel atölyenin kuruluşu — İstanbul"],
              ["2021", "Frekans & Ses Şifası Master Programı (Almanya)"],
              ["2024", "Online Eğitim Programları — 200+ mezun"],
            ].map(([y, t]) => (
              <li key={y} className="relative">
                <span className="absolute -left-[34px] sm:-left-[46px] top-1 w-3 h-3 rounded-full bg-gold border border-cream-50" />
                <div className="font-display italic text-gold text-2xl">{y}</div>
                <div className="font-display text-xl sm:text-2xl md:text-3xl text-midnight mt-1 font-normal">
                  {t}
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 bg-midnight text-cream-50 relative overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-gold/20 blur-[100px]" />
        <div className="relative page-shell page-shell-narrow grid grid-cols-12 gap-8 md:gap-10 items-center">
          <div className="col-span-12 md:col-span-8">
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-light leading-[1.05]">
              Senin yolculuğun da
              <br />
              <em className="text-gold-soft">burada</em> başlayabilir.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:text-right">
            <Link
              to="/randevu"
              data-testid="about-cta-appointment"
              className="inline-flex w-full sm:w-auto items-center justify-center gap-3 px-6 sm:px-10 py-4 bg-cream-50 text-midnight rounded-full text-xs sm:text-sm tracking-[0.14em] md:tracking-[0.22em] uppercase hover:bg-gold hover:text-cream-50 transition-all duration-500"
            >
              Randevu Al <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
